import { useEffect, useRef, useState } from 'react';
import { useResetTrack, useTrackProgress } from '@/hooks/useLearningProgress';

type CompletedTrack = {
  slug: string;
  name: string;
  total: number;
};

/**
 * Watches every learning section (Forex พื้นฐาน, วิธีเทรด, MT4/MT5, วิเคราะห์, ความเสี่ยง).
 * The moment any section flips from incomplete to complete, it shows a small
 * confirmation prompt so the learner can restart that section or keep the progress.
 */
export default function LearningCompletionPrompt() {
  const tracks = useTrackProgress();
  const resetTrack = useResetTrack();
  const [active, setActive] = useState<CompletedTrack | null>(null);
  const prevComplete = useRef<Map<string, boolean> | null>(null);

  useEffect(() => {
    const current = new Map(tracks.map((track) => [track.slug, track.isComplete]));

    // First render: remember the current state without prompting (e.g. page reload).
    if (prevComplete.current === null) {
      prevComplete.current = current;
      return;
    }

    const justCompleted = tracks.find(
      (track) => track.isComplete && !prevComplete.current?.get(track.slug),
    );
    if (justCompleted) {
      setActive({ slug: justCompleted.slug, name: justCompleted.name, total: justCompleted.total });
    }

    prevComplete.current = current;
  }, [tracks]);

  if (!active) return null;

  const handleRestart = () => {
    resetTrack(active.slug);
    setActive(null);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="learning-complete-title"
    >
      <button
        type="button"
        aria-label="ปิด"
        onClick={() => setActive(null)}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50 backdrop-blur-sm"
      />

      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-background-200 bg-background-50 p-6 text-center animate-fade-in md:p-8">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-trophy-line text-3xl" />
        </span>

        <h2
          id="learning-complete-title"
          className="mt-5 font-heading text-xl font-semibold text-foreground-950"
        >
          ยินดีด้วย! เรียนครบทุกบทความแล้ว
        </h2>

        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          คุณอ่านครบทั้ง {active.total} บทความของหมวด &ldquo;{active.name}&rdquo; แล้ว
          ต้องการเริ่มเรียนหมวดนี้ใหม่ (ล้างเครื่องหมาย &ldquo;อ่านแล้ว&rdquo; ทั้งหมด)
          หรือเก็บความคืบหน้าไว้ก่อน?
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleRestart}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line text-base" />
            เริ่มเรียนใหม่
          </button>
          <button
            type="button"
            onClick={() => setActive(null)}
            className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-bookmark-line text-base" />
            เก็บไว้ก่อน
          </button>
        </div>
      </div>
    </div>
  );
}